import Reporter from './Reporter';
import Signal from './Signal';
import { cancelifyPromise } from './async';
import { ObservableValue, IObservableValuePublic } from './ObservableValue';
import TokenizedArray from './TokenizedArray';

type CleanupFunction = () => void;
type DomElement = Document | Element | Window;
type DomEventListener = Exclude<
    Parameters<DomElement["addEventListener"]>[1],
    EventListenerObject
>;

export default class CleanupManager {
    private readonly registry = new TokenizedArray<CleanupFunction>();

    private readonly onCleanUpAll = new Signal();

    private cleanUpAllRunningCounter = 0;
    public readonly reporter = new Reporter();

    // High level helpers

    public cancelifyPromise<T>(promise: Promise<T>): Promise<T> {
        const token = this.getToken();
        const result = cancelifyPromise(() => token.getValue(), promise);

        result.finally(() => {
            if (token.getValue()) {
                this.reporter.report('Promise finished despite cancel', { once: true });
            }
        });

        return result;
    }

    public add(...cleanups: Array<CleanupFunction>): CleanupFunction {
        const undoAddOnes = cleanups.map(cleanUp => this.addOne(cleanUp));
        return () => {
            while (undoAddOnes.length > 0) {
                const undoAddOne = undoAddOnes.pop()!;
                undoAddOne();
            }
        };
    }

    public addDOMEventHandler(
        domElement: DomElement,
        eventName: string,
        handler: DomEventListener
    ): CleanupFunction {
        const wrappedHandler: DomEventListener = (...args) => handler(...args);
        domElement.addEventListener(eventName, wrappedHandler);
        return this.addOne(() => {
            domElement.removeEventListener(eventName, wrappedHandler);
        });
    }

    // Lower level functionality

    private addOne(cleanup: CleanupFunction): CleanupFunction {
        if (this.cleanUpAllRunningCounter > 0) {
            this.reporter.report(
                'adding listener during cleanUpAll - scheduling it for execution',
                { once: true }
            );
        }

        const indexToken = this.registry.add(cleanup);
        return () => {
            if (this.registry.safeRemove(indexToken)) {
                cleanup();
            }
        };
    }

    public getToken(): IObservableValuePublic<boolean> {
        if (this.cleanUpAllRunningCounter > 0) {
            this.reporter.report('getting token during cleanUpAll', { once: true });
        }

        const result = new ObservableValue(false);
        this.onCleanUpAll.addListenerOnce(() => { result.control.setValue(true); });
        return result.read;
    }

    public cleanUpAll(): void {
        if (this.cleanUpAllRunningCounter > 0) {
            this.reporter.report('cleanUpAll went recursive', { once: true });
        }
        this.cleanUpAllRunningCounter += 1;

        this.onCleanUpAll.dispatch();

        while (this.registry.getLength() > 0) {
            const lastCleanUp = this.registry.pop()!;
            lastCleanUp();
        }

        this.cleanUpAllRunningCounter -= 1;
    }
}
